import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../css/PartnerLogin.css';
import { API_BASE } from '../config';

const PartnerLogin = () => {
  const navigate = useNavigate();

  // Hardcoded Admin Password Configuration
  const ADMIN_PASSWORD = "Admin@B2B"; // Isko aap apne hisab se change kar sakte hain

  // State Management
  const [roleType, setRoleType] = useState('');
  const [subRoleType, setSubRoleType] = useState('');
  const [partnerList, setPartnerList] = useState([]);
  const [selectedUsername, setSelectedUsername] = useState('');
  const [userCode, setUserCode] = useState('');
  const [adminPassword, setAdminPassword] = useState(''); // Admin ke liye password state
  
  const [loadingList, setLoadingList] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Role / SubRole Change hone par Partners Directory Fetch karna
  useEffect(() => {
    const fetchPartners = async () => {
      setSelectedUsername('');
      setPartnerList([]);
      setError('');
      setAdminPassword('');
      setUserCode('');

      // Agar Admin select kiya hai ya role empty hai toh API hit nahi karni
      if (!roleType || roleType === 'Admin') return;
      if (roleType === 'Buyer' && !subRoleType) return;

      try {
        setLoadingList(true);
        const queryParams = new URLSearchParams({ roleType });
        if (roleType === 'Buyer' && subRoleType) {
          queryParams.append('subRoleType', subRoleType);
        }

        const response = await axios.get(`${API_BASE}/api/partners/list-by-role?${queryParams.toString()}`);
        
        if (response.data.success) {
          setPartnerList(response.data.data);
        }
      } catch (err) {
        setError('Failed to load user directory for selected category.');
      } finally {
        setLoadingList(false);
      }
    };

    fetchPartners();
  }, [roleType, subRoleType]);

  // Main Authentication Handler
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // ------------------------------------------
    // CASE A: ADMIN HARDCODED AUTHENTICATION
    // ------------------------------------------
    if (roleType === 'Admin') {
      if (adminPassword === ADMIN_PASSWORD) {
        localStorage.setItem('adminSession', 'true');
        navigate('/admin'); // Aapka banaya hua admin dashboard route
        return;
      } else {
        setError('❌ Invalid Admin Password. Access Denied!');
        return;
      }
    }

    // ------------------------------------------
    // CASE B: NORMAL PARTNER AUTHENTICATION
    // ------------------------------------------
    if (!selectedUsername || !userCode) {
      setError('⚠️ Please select your username and enter your secret code.');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await axios.post(`${API_BASE}/api/partners/login-verify`, {
        username: selectedUsername,
        userCode: userCode
      });

      if (response.data.success) {
        localStorage.setItem('partnerProfile', JSON.stringify(response.data.profile));
        navigate('/partner-dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid Username or Verification Secret Code!');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-master-container">
      <div className="login-card shadow-lg">
        <div className="login-header">
          <h2>🔒 Portal Access</h2>
          <p>Select your category, verify your identity & access your terminal.</p>
        </div>

        {error && <div className="login-error-alert">{error}</div>}

        <form onSubmit={handleLoginSubmit} className="login-form">
          {/* STEP 1: SELECT MAIN ROLE */}
          <div className="form-group">
            <label>1. Select Account Type <span className="required">*</span></label>
            <select 
              value={roleType} 
              onChange={(e) => { setRoleType(e.target.value); setSubRoleType(''); }}
              required
            >
              <option value="">-- Choose Category --</option>
              <option value="Admin">🛠️ Management Admin</option>
              <option value="Vendor">Vendor</option>
              <option value="Buyer">Buyer</option>
            </select>
          </div>

          {/* STEP 2: SUB-ROLE (ONLY FOR BUYERS) */}
          {roleType === 'Buyer' && (
            <div className="form-group">
              <label>Select Buyer Category <span className="required">*</span></label>
              <select 
                value={subRoleType} 
                onChange={(e) => setSubRoleType(e.target.value)}
                required
              >
                <option value="">-- Choose Buyer Category --</option>
                <option value="Sales Agent">Sales Agent (Commission Based)</option>
                <option value="Brand Distributor">Brand Distributor</option>
                <option value="Wholesale Loyal Partner Club">Wholesale Loyal Partner Club</option>
                <option value="Brand Loyal Partner Club">Brand Loyal Partner Club</option>
              </select>
            </div>
          )}

          {/* STEP 3 (ADMIN CASE): SHOW PASSWORD INPUT */}
          {roleType === 'Admin' && (
            <div className="form-group">
              <label>Enter Admin Control Password <span className="required">*</span></label>
              <input 
                type="password"
                placeholder="••••••••"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                required
              />
            </div>
          )}

          {/* STEP 3 (PARTNER CASE): SELECT USER FROM LIST */}
          {roleType && roleType !== 'Admin' && (roleType !== 'Buyer' || subRoleType) && (
            <div className="form-group">
              <label>2. Select Your Account Name <span className="required">*</span></label>
              {loadingList ? (
                <div className="loading-spinner">Fetching registered directory...</div>
              ) : (
                <select 
                  value={selectedUsername} 
                  onChange={(e) => setSelectedUsername(e.target.value)}
                  required
                >
                  <option value="">
                    {partnerList.length === 0 ? 'No registered users found in this role' : '-- Select Your Company/Name --'}
                  </option>
                  {partnerList.map((partner) => (
                    <option key={partner.id} value={partner.username}>
                      {partner.companyName} ({partner.username})
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* STEP 4 (PARTNER CASE): ENTER SECRET CODE */}
          {roleType !== 'Admin' && selectedUsername && (
            <div className="form-group">
              <label>3. Enter 6-Digit Secret User Code <span className="required">*</span></label>
              <input 
                type="password"
                maxLength={6}
                placeholder="Ex: 489201"
                value={userCode}
                onChange={(e) => setUserCode(e.target.value)}
                required
              />
            </div>
          )}

          {/* DYNAMIC BUTTON TEXT */}
          <button 
            type="submit" 
            className="btn-login-submit"
            disabled={isSubmitting || (roleType !== 'Admin' && (!selectedUsername || !userCode)) || (roleType === 'Admin' && !adminPassword)}
          >
            {isSubmitting ? 'Verifying...' : roleType === 'Admin' ? 'Enter Admin Panel 🛠️' : 'Access Dashboard 🚀'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PartnerLogin;